import * as React from "react";
import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Icon, type IconName } from "../../components/ui/icon";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../../components/ui/menu";
import { Text } from "../../components/ui/text";
import type { Omittable } from "../../lib/props";

/** One way of filling an empty section, offered from the action row's menu. */
export type WidgetEmptyActionItem = Omittable<{
  icon?: IconName;
  label: string;
  onClick: () => void;
}>;

/**
 * What a body declares when it has nothing to show and ONE row the reader can
 * press about it. The kit renders it; the module decides when it applies.
 *
 * The row either does one thing (`onClick`) or opens a short menu of the ways
 * the section can be filled (`items` — "Create…", "Add existing…"), so a
 * section with two doors still reads as ONE row in the header's place instead
 * of a header over a list of rows.
 */
export type WidgetEmptyActionSpec = Omittable<{
  /** The row's glyph. Defaults to the module's own icon inside a card. */
  icon?: IconName;
  /** A verb phrase — "Add fields", "Attach file" — never the section noun. */
  label: string;
  /** The row's one action. Ignored when `items` is given. */
  onClick?: () => void;
  /** The menu the row opens instead of acting directly. */
  items?: WidgetEmptyActionItem[];
}>;

/**
 * The action row's press surface, in whichever role the spec asks for: a plain
 * button firing `onClick`, or the trigger of a menu listing `items`. `row` is
 * the element that wears the row's look — the card's header capsule or the
 * ghost {@link WidgetEmptyActionRow} — and receives the press.
 */
export function WidgetEmptyActionPress({
  spec,
  row,
}: {
  spec: WidgetEmptyActionSpec;
  row: React.ReactElement<Record<string, unknown>>;
}) {
  if (!spec.items || spec.items.length === 0) {
    return React.cloneElement(row, { onClick: spec.onClick });
  }
  return (
    <Menu>
      <MenuTrigger render={row} />
      <MenuPopup align="start">
        {spec.items.map((item) => (
          <MenuItem key={item.label} onClick={item.onClick}>
            {item.icon ? <Icon name={item.icon} size="md" /> : null}
            <Text>{item.label}</Text>
          </MenuItem>
        ))}
      </MenuPopup>
    </Menu>
  );
}

/**
 * The seam between a card and the one empty action its body may declare.
 * `WidgetCard` provides it; `hosted` says whether the card's header can take
 * the row's place (a stack card with a header), and `set` is how the body's
 * {@link WidgetEmptyAction} hands the card its spec while mounted.
 */
export type WidgetEmptyActionHost = Omittable<{
  hosted: boolean;
  icon?: IconName;
  set: (spec: WidgetEmptyActionSpec | null) => void;
}>;

const WidgetEmptyActionHostContext = React.createContext<WidgetEmptyActionHost | null>(
  null,
);

export function WidgetEmptyActionHostProvider({
  value,
  children,
}: {
  value: WidgetEmptyActionHost;
  children: React.ReactNode;
}) {
  return (
    <WidgetEmptyActionHostContext.Provider value={value}>
      {children}
    </WidgetEmptyActionHostContext.Provider>
  );
}

export type WidgetEmptyActionRowProps = Omittable<{
  icon?: IconName;
  label: string;
}> &
  Omit<
    React.ComponentProps<typeof Button>,
    "align" | "children" | "fill" | "size" | "variant"
  >;

/**
 * One quiet ghost action row standing in for the list rows that are not there
 * yet — muted glyph + label, left-aligned, hover wash — so the row IS the
 * guidance and no centered well inflates the tile. Rest props spread into the
 * Button so the row works as a selector or popover `trigger` element: Base UI's
 * render merge lands its handlers, aria state and ref here, and they must reach
 * the real button.
 *
 * A body with SEVERAL things the reader can do lists one of these per action.
 * A body with exactly one renders {@link WidgetEmptyAction} instead, so the
 * card can lift it into the header.
 */
export function WidgetEmptyActionRow({ icon, label, ...props }: WidgetEmptyActionRowProps) {
  return (
    <Button justify="start" fullWidth size="sm" variant="ghost" {...props}>
      {icon ? <Icon color="muted" name={icon} size="sm" /> : null}
      <Text color="muted" size="sm">
        {label}
      </Text>
    </Button>
  );
}

/**
 * The body's declaration that the section is empty and this is what fills it.
 *
 * Inside a stack card with a header, the row does not render here at all: the
 * CARD takes the spec and its header becomes the action row — same capsule,
 * same rung, same hover wash as the disclosure header it stands in for, so the
 * section reads as one row that changes role, not as a header over an empty
 * body. The moment the body stops rendering this (the first item landed), the
 * header returns to its title and disclosure, and a collapsed card opens so
 * the reader sees what they just added.
 *
 * Anywhere else (a grid tile, a headerless stack card, a body rendered with no
 * card) it renders as a {@link WidgetEmptyActionRow} in the body.
 *
 * Render exactly one per body. Two registrations against one card would race,
 * and a section with two things to offer wants two visible rows, not a header
 * that hides one.
 */
export function WidgetEmptyAction({ icon, label, onClick, items }: WidgetEmptyActionSpec) {
  const host = React.useContext(WidgetEmptyActionHostContext);
  const hosted = host?.hosted ?? false;
  const set = host?.set;
  // Presses reach the latest handlers through a ref, so the registration
  // effect keys on the spec's visible parts only (label, icons) and a
  // re-rendered closure never re-registers (and re-renders the card) on every
  // body render.
  const handlersRef = React.useRef({ onClick, items });
  handlersRef.current = { onClick, items };
  const press = React.useCallback(() => handlersRef.current.onClick?.(), []);
  // The menu's visible shape, as a string, so a body that rebuilds the item
  // array each render still registers once.
  const itemsKey = items?.map((item) => `${item.icon ?? ""}:${item.label}`).join("|");
  const stableItems = React.useMemo<WidgetEmptyActionItem[] | undefined>(
    () =>
      items?.map((item, index) => ({
        icon: item.icon,
        label: item.label,
        onClick: () => handlersRef.current.items?.[index]?.onClick(),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `itemsKey` stands for `items`' visible parts; handlers ride the ref.
    [itemsKey],
  );

  // Layout effect: the card swaps its header in the same commit the body
  // settles, so a title never flashes before the action row replaces it.
  React.useLayoutEffect(() => {
    if (!hosted || !set) return;
    set({ icon, label, onClick: press, items: stableItems });
    return () => set(null);
  }, [hosted, set, icon, label, press, stableItems]);

  if (hosted) return null;
  return (
    <WidgetEmptyActionPress
      spec={{ icon, label, onClick: press, items: stableItems }}
      row={<WidgetEmptyActionRow icon={icon ?? host?.icon} label={label} />}
    />
  );
}

export type WidgetEmptyChoiceRowProps = Omittable<{
  /** The choice's glyph, on the leading edge. */
  icon?: IconName;
  /** The choice itself — a noun ("Google Calendar"), not the verb. */
  label: string;
  /** The verb on the trailing button — "Connect", "Enable", "Add". */
  actionLabel: string;
  onAction: () => void;
  disabled?: boolean;
}>;

/**
 * One of several EQUAL choices an empty body offers, each with its own verb:
 * a bordered elevated tile — leading glyph, the choice's name left-aligned,
 * the action as a quiet secondary button on the trailing edge. Where
 * {@link WidgetEmptyActionRow} is a list of next steps that reads as text, a
 * choice row is a card the reader picks between — an integration to connect,
 * a source to enable — so the option and its verb are separated on purpose.
 * Stack them in a column; the column's width is the caller's.
 */
export function WidgetEmptyChoiceRow({
  icon,
  label,
  actionLabel,
  onAction,
  disabled,
}: WidgetEmptyChoiceRowProps) {
  return (
    <Box
      direction="row"
      align="center"
      gap="sm"
      paddingX="sm"
      paddingY="xs"
      rounded="lg"
      border
      surface="elevated"
      width="100%"
      minWidth={0}
    >
      {icon ? <Icon color="muted" name={icon} /> : null}
      <Text size="sm" weight="medium" truncate className="flex-1 text-left">
        {label}
      </Text>
      <Button disabled={disabled} onClick={onAction} size="sm" color="neutral" ink="foreground">
        {actionLabel}
      </Button>
    </Box>
  );
}
